/** The backend envelope. Every endpoint answers in this shape. */
export interface ApiEnvelope<T> {
  isSuccess: boolean;
  error: string | null;
  errorCode: string | null;
  result: T | null;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  /** Parsed and sent as a JSON body. Ignored for GET/DELETE. */
  body?: unknown;
  /** Appended as a query string; `undefined` and `null` entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Do not attach the Authorization header (login, refresh, public endpoints). */
  skipAuth?: boolean;
  /** Do not attempt a token refresh on 401 (used by the refresh call itself). */
  skipRefresh?: boolean;
  /**
   * Stable idempotency key for this logical operation. Mutations get one
   * automatically; pass your own to keep it stable across user-driven retries
   * of the *same* submission.
   */
  idempotencyKey?: string;
  /** Overrides the default request timeout in ms. */
  timeoutMs?: number;
}

export interface ClientConfig {
  baseUrl: string;
  /** Default per-request timeout. */
  timeoutMs?: number;
  /** Path used to refresh the access token. */
  refreshPath?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string | null;
  /** Unix seconds, when the backend tells us. */
  expiresAt?: number | null;
}

export interface RefreshResult {
  accessToken: string;
  /** Some backends rotate the refresh token; some do not. Both are handled. */
  newRefreshToken?: string | null;
  refreshToken?: string | null;
  expiresAt?: number | null;
  /**
   * Novix.Api answers with an ISO-8601 `accessTokenExpiresAt` rather than a Unix
   * `expiresAt`. Both shapes are accepted so the client is not coupled to one
   * backend's serialiser.
   */
  accessTokenExpiresAt?: string | null;
}

/** Parses either a Unix-seconds number or an ISO-8601 instant into Unix seconds. */
export function toUnixSeconds(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000);
}
